import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ChevronRight, FileText, Plus } from 'lucide-react';
import { losApi, LOAN_STATUS_LABELS, PROGRAM_LABELS } from './losApi';
import { formatDate } from './kit/time';
import { useAuth } from './AuthContext';

const fmt$ = (n) => n ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : null;
const when = (iso) => iso ? formatDate(iso) : '—';
const addrLine = (a) => a ? [a.street, a.city, a.state].filter(Boolean).join(', ') : '';

// Contact profile → Loans tab: every loan file whose primary borrower is this
// contact (the reverse of the loan → contact link added in ticket 32).
// Rows deep-link into the loan file. "Start a loan" opens the new-loan form on
// Loan Files with this contact already picked as the borrower (ticket 188 row 7);
// only seats that can create a loan (`loans.edit`) see it.
export default function ContactLoans({ contactId }) {
  const { user } = useAuth() || {};
  const canStart = (user?.permissions || []).includes('loans.edit');
  const [loans, setLoans] = useState(null);

  useEffect(() => {
    let alive = true;
    losApi.listLoans({ contact_id: contactId })
      .then(list => { if (alive) setLoans(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setLoans([]); });
    return () => { alive = false; };
  }, [contactId]);

  const startHref = `/portal/loans?new=1&contact_id=${encodeURIComponent(contactId)}`;
  const startBtn = canStart && (
    <Link
      to={startHref}
      data-testid="contact-loans-start"
      className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition shadow-sm"
      style={{ background: '#070B5F' }}
    >
      <Plus size={14} /> Start a loan
    </Link>
  );

  if (loans === null) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="contact-loans-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading loans...
      </div>
    );
  }
  if (loans.length === 0) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-400" data-testid="contact-loans-empty">
        <p className="text-sm">No loan files yet.</p>
        {canStart && <p className="text-xs mt-1 mb-4">Start one with this contact as the borrower.</p>}
        {startBtn}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {canStart && <div className="flex justify-end">{startBtn}</div>}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="contact-loans-list">
        {loans.map(l => (
          <Link
            key={l.loan_file_id}
            to={`/portal/loans/${l.loan_file_id}`}
            className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition"
            data-testid={`contact-loan-${l.loan_file_id}`}
          >
            <FileText size={14} className="text-slate-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: '#070B5F' }}>
                {addrLine(l.property_address) || l.loan_number}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {l.loan_number} · {PROGRAM_LABELS[l.loan_program] || l.loan_program} · {LOAN_STATUS_LABELS[l.status] || l.status}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              {fmt$(l.loan_amount) && <div className="text-sm font-bold" style={{ color: '#6BA82E' }}>{fmt$(l.loan_amount)}</div>}
              <div className="text-[11px] text-slate-400">Updated {when(l.updated_at)}</div>
            </div>
            <ChevronRight size={14} className="text-slate-300 flex-shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  );
}
